import { useEffect, useState } from "react";
import { Facebook, Instagram, Phone, Mail, MapPin, Compass } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";

export default function Footer() {
  const [content, setContent] = useState({});
  const [modal, setModal] = useState(null);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const policies = {
    privacy: { title: "Privacy Policy", body: content.privacy_policy },
    terms: { title: "Terms", body: content.terms },
    cancellation: { title: "Cancellation Policy", body: content.cancellation_policy },
  };

  return (
    <footer className="relative bg-navy text-cream/85 pt-20 pb-10 overflow-hidden">
      <div className="absolute inset-0 fn-scales opacity-40" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-6 grid gap-12 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <img src="/assets/logo.png" alt="Float Now" className="h-16 w-16 object-contain" />
            <div>
              <p className="font-serif text-2xl font-bold text-cream">Float Now</p>
              <p className="text-xs tracking-[0.18em] uppercase text-gold">Mermaids &amp; Pirates of North Platte</p>
            </div>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-cream/70">
            {content.tagline}
          </p>
          <div className="mt-5 rounded-2xl border border-gold/30 bg-white/5 p-4 text-xs text-cream/70">
            Water-safety disclaimer: No child is ever drown-proof. Swim lessons add an important layer of protection, but active adult supervision and multiple layers of water safety are always essential.
          </div>
        </div>

        <div>
          <p className="font-serif text-lg text-cream mb-4">Get in Touch</p>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-2"><MapPin size={16} className="text-teal" /> {content.address}</li>
            <li className="flex items-center gap-2"><Phone size={16} className="text-teal" /> {content.phone}</li>
            <li className="flex items-center gap-2"><Mail size={16} className="text-teal" /> {content.email}</li>
          </ul>
          <div className="flex gap-3 mt-5">
            <a href={content.facebook || "#"} target="_blank" rel="noreferrer" data-testid="footer-facebook" className="h-10 w-10 grid place-items-center rounded-full border border-gold/40 hover:bg-teal transition-colors"><Facebook size={18} /></a>
            <a href={content.instagram || "#"} target="_blank" rel="noreferrer" data-testid="footer-instagram" className="h-10 w-10 grid place-items-center rounded-full border border-gold/40 hover:bg-teal transition-colors"><Instagram size={18} /></a>
          </div>
        </div>

        <div>
          <p className="font-serif text-lg text-cream mb-4 flex items-center gap-2"><Compass size={18} className="text-gold" /> Policies</p>
          <ul className="space-y-3 text-sm">
            <li><button onClick={() => setModal("privacy")} data-testid="footer-privacy" className="hover:text-gold transition-colors">Privacy Policy</button></li>
            <li><button onClick={() => setModal("terms")} data-testid="footer-terms" className="hover:text-gold transition-colors">Terms</button></li>
            <li><button onClick={() => setModal("cancellation")} data-testid="footer-cancellation" className="hover:text-gold transition-colors">Cancellation Policy</button></li>
          </ul>
        </div>
      </div>

      <div className="relative mx-auto max-w-7xl px-6 mt-14 pt-6 border-t border-cream/15 text-xs text-cream/50">
        © {new Date().getFullYear()} Float Now — Mermaids &amp; Pirates of North Platte. All rights reserved.
      </div>

      <Dialog open={!!modal} onOpenChange={() => setModal(null)}>
        <DialogContent className="bg-cream max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif text-navy text-2xl">{modal && policies[modal].title}</DialogTitle></DialogHeader>
          <p className="text-navy/80 leading-relaxed whitespace-pre-line text-sm">{modal && policies[modal].body}</p>
        </DialogContent>
      </Dialog>
    </footer>
  );
}
